const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: string;
}

async function request<T>(endpoint: string, options?: RequestOptions): Promise<T> {
  const token = localStorage.getItem('onisland_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options?.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers,
    ...(options || {}),
    body: options?.body != null && options.body !== '' ? JSON.stringify(options.body) : undefined,
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `API error: ${res.status}`);
  }
  
  return res.json();
}

export interface ApiEvent {
  id: number;
  title: string;
  description: string;
  event_date: string;
  venue_name: string;
  venue_address?: string;
  kind: string;
  genre?: string;
  price: string;
  age_restriction?: string;
  bar_policy?: string;
  accessibility_notes?: string;
  needs_volunteers?: number;
  host_name?: string;
  organiser_name?: string;
  is_curated: boolean;
  ticket_tailor_event_id?: string;
  ticketing_mode?: string;
  ticket_url?: string;
}

export interface ApiMember {
  id: number;
  email: string;
  display_name: string;
  role: string;
  vouch_count: number;
  status: string;
  vouched_by_name?: string | null;
}

export interface ApiOrganisation {
  id: number;
  name: string;
  description: string | null;
  website: string | null;
  contact_email: string | null;
  ticket_tailor_account_ref: string | null;
  is_concierge: boolean;
  created_at: string;
}

export interface ApiArtist {
  id: number;
  name: string;
  bio: string | null;
  music_genre: string | null;
  website: string | null;
  social_links: unknown;
  member_id: number | null;
  created_at: string;
  member_name?: string | null;
}

export interface ApiArtistAvailability {
  id: number;
  artist_id: number;
  start_at: string;
  end_at: string;
  status: 'available' | 'unavailable';
  created_at: string;
}

export interface ApiTicketSales {
  total_tickets: number;
  valid_tickets: number;
  checked_in: number;
  cancelled: number;
  daily_sales: Array<{ date: string; revenue: number; tickets: number }>;
  ticket_status: Array<{ status: string; count: number }>;
  member_breakdown: Array<{ member_name: string; tickets: number }>;
}

export interface ApiTicket {
  id: number;
  status: string;
  member_name?: string;
  checked_in_at?: string;
  checked_in_by_name?: string;
}

export interface CheckIn {
  id: number;
  ticket_id: number;
  member_id: number;
  checked_in_at: string;
  checked_out_at?: string | null;
  status: 'checked_in' | 'checked_out' | 'not_checked_in';
  member_name?: string;
  email?: string;
  ticket_tailor_id?: string;
  event_title?: string;
  event_date?: string;
}

export interface ApiIncident {
  id: number;
  incident_type: string;
  description: string;
  status: string;
  created_at: string;
}

export interface ApiAuthResponse {
  user: { id: number; email: string; display_name: string; role: string };
  token: string;
}

export interface Volunteer {
  id: number;
  member_name: string;
  email: string;
  role: string;
  hours_shifted: number;
  hours_planned: number;
  status: 'pending' | 'confirmed' | 'assigned' | 'completed' | 'cancelled';
  notes: string;
  created_at: string;
}

export interface DashboardData {
  event_id: string;
  stats: {
    tickets_sold: number;
    revenue: number;
    volunteers_count: number;
    incidents_count: number;
  };
  budget: Array<{ category: string; total: number; is_expense: boolean; approved_count: number; pending_count: number }>;
  volunteers: Array<{ role: string; count: number; planned_hours: number }>;
  approval?: { approved: number; pending: number; total: number };
}

export interface ApiCheckoutResponse {
  checkoutUrl: string;
  tickets: { id: number; event_id: number; ticket_tailor_id: string }[];
}

export const api = {
  auth: {
    login: (email: string, password: string): Promise<{ data: ApiAuthResponse; error: string }> =>
      fetchAuth('/auth/login', { method: 'POST', body: { email, password } }),
    register: (email: string, password: string, displayName: string): Promise<{ data: ApiAuthResponse; error: string }> =>
      fetchAuth('/auth/register', { method: 'POST', body: { email, password, displayName } }),
    profile: (): Promise<ApiMember> => request('/auth/profile'),
  },
  events: {
    list: (params?: { kind?: string; genre?: string }) => {
      const qs = new URLSearchParams();
      if (params?.kind) qs.set('kind', params.kind);
      if (params?.genre) qs.set('genre', params.genre);
      const query = qs.toString();
      return request<ApiEvent[]>('/events' + (query ? `?${query}` : ''));
    },
    get: (id: number) => request<ApiEvent>(`/events/${id}`),
    create: (data: {
      title: string
      description: string
      event_date: string
      venue_name: string
      venue_address?: string
      kind: string
      genre?: string
      price: string
      ticketing_mode?: string
      ticket_url?: string
      is_curated: boolean
    }) => request<ApiEvent>('/events', { method: 'POST', body: data }),
    update: (id: number, data: Partial<{ title: string; description: string; event_date: string; venue_name: string; venue_address: string; kind: string; genre: string; price: string; age_restriction: string; bar_policy: string; accessibility_notes: string; needs_volunteers: number; is_curated: boolean; ticketing_mode: string; ticket_url: string }>) => request<ApiEvent>(`/events/${id}`, { method: 'PUT', body: data }),
    sync: () => request<{ success: boolean; synced: number }>('/events/sync', { method: 'POST' }),
    syncTickets: (id: number) => request<{ success: boolean; synced: number }>(`/events/${id}/sync-tickets`, { method: 'POST' }),
    dashboard: (id: number) => request<DashboardData>(`/events/${id}/dashboard`),
  },
  members: {
    list: () => request<ApiMember[]>('/members'),
    vouch: (id: number) => request<{ id: number; vouch_count: number }>(`/members/${id}/vouch`, { method: 'POST' }),
  },
  artists: {
    list: () => request<ApiArtist[]>('/artists'),
    get: (id: number) => request<ApiArtist>(`/artists/${id}`),
    create: (data: { name: string; bio?: string | null; musicGenre?: string | null; website?: string | null; socialLinks?: unknown | null; memberId?: number | null }) => request<ApiArtist>('/artists', { method: 'POST', body: data }),
    listAvailability: (id: number) => request<ApiArtistAvailability[]>(`/artists/${id}/availability`),
    addAvailability: (id: number, data: { startAt: string; endAt: string; status?: string }) => request<ApiArtistAvailability>(`/artists/${id}/availability`, { method: 'POST', body: data }),
  },
  organisations: {
    list: () => request<ApiOrganisation[]>('/organisations'),
    create: (name: string, description: string | null, website: string | null, contactEmail: string | null, ticketTailorAccountRef: string | null) => request<ApiOrganisation>(`/organisations`, { method: 'POST', body: { name, description, website, contactEmail, ticketTailorAccountRef } }),
    update: (id: number, patch: { isConcierge?: boolean; name?: string; description?: string | null; website?: string | null; contactEmail?: string | null; ticketTailorAccountRef?: string | null }) => request<ApiOrganisation>(`/organisations/${id}`, { method: 'PATCH', body: patch }),
  },
  following: {
    list: () => request<{ id: number; follower_id: number; following_id: number; following_type: string; created_at: string; display_name: string | null }[]>('/following'),
    unfollow: (id: number) => request<{ deleted: boolean }>(`/following/${id}`, { method: 'DELETE' }),
    follow: (followingId: number, followingType?: string) => request<{ id: number; follower_id: number; following_id: number; following_type: string; created_at: string; display_name: string | null }>(`/following`, { method: 'POST', body: { followingId, followingType } }),
  },
  settings: {
    communityFee: () => request<{ fee: string }>('/settings/community-fee'),
    updateCommunityFee: (fee: string) => request<{ fee: string }>('/settings/community-fee', { method: 'PUT', body: { fee } }),
  },
  concierge: {
    send: (message: string, history?: ApiChatMessage[]) => request<ApiConciergeResponse>('/concierge', { method: 'POST', body: { message, history } }),
    faq: () => request<{ faqs: Array<{ category: string; response: string; keywords: string[] }> }>('/concierge/faq'),
  },
  budget: {
    list: (eventId: number) => request<{
      event_id: number;
      lines: Array<{
        id: number;
        description: string;
        amount: number;
        category: string;
        is_expense: boolean;
        is_approved: boolean;
        synced_from_tt: boolean;
      }>;
      totals: { total_expenses: number; total_income: number; net_budget: number };
      actuals: { actual_revenue: number };
    }>(`/budget/event/${eventId}`),
    forecast: (eventId: number) => request<{
      event_id: number;
      event_title: string;
      ticket_price: number;
      sales: { valid_tickets: number; pending_tickets: number; cancelled_tickets: number };
      forecast: { current_revenue: number; potential_revenue: number; projected_revenue: number };
    }>(`/budget/event/${eventId}/forecast`),
    approve: (id: number) => request<{
      id: number;
      description: string;
      amount: number;
      category: string;
      is_expense: boolean;
      is_approved: boolean;
      synced_from_tt: boolean;
    }>(`/budget/${id}/approve`, { method: 'POST' }),
    create: (data: { eventId: number; description: string; amount: number; category: string; isExpense?: boolean }) => request<{ id: number; description: string; amount: number; category: string; is_expense: boolean; is_approved: boolean }>(`/budget`, { method: 'POST', body: data }),
    delete: (id: number) => request<{ deleted: boolean }>(`/budget/${id}`, { method: 'DELETE' }),
    update: (id: number, data: Partial<{ description: string; amount: number; category: string; isExpense: boolean; isApproved: boolean }>) => request<{ id: number; description: string; amount: number; category: string; is_expense: boolean; is_approved: boolean }>(`/budget/${id}`, { method: 'PUT', body: data }),
  },
  tickets: {
    list: (eventId: number) => request<ApiTicket[]>(`/tickets/event/${eventId}`),
    checkout: (eventId: number, qty: number) =>
      request<ApiCheckoutResponse>('/tickets/checkout', { method: 'POST', body: { eventId, qty } }),
    // id is the check_ins row id, not the ticket id
    checkIn: (id: number) => request<{ id: number; ticket_id: number; member_id: number; checked_in_at: string; status: string }>(`/tickets/${id}/check-in`, { method: 'POST' }),
  },
  incidents: {
    create: (data: { incident_type: string; description: string; eventId?: number }) =>
      request<ApiIncident>('/incidents', { method: 'POST', body: data }),
    list: (eventId: number) =>
      request<{ id: number; incident_type: string; description: string; status: string; is_confidential: boolean; resolution_notes: string | null; created_at: string; reporter_name: string | null; assigned_to: number | null; assigned_to_name: string | null }[]>(`/incidents?event_id=${eventId}`),
    updateStatus: (id: number, data: { status: string; resolutionNotes?: string }) =>
      request<ApiIncident>(`/incidents/${id}`, { method: 'PUT', body: data }),
    assign: (id: number, staffId: number) =>
      request<{ id: number; assigned_to: number; status: string }>(`/incidents/${id}/assign`, { method: 'POST', body: { staffId } }),
  },
  reports: {
    exportEvent: (id: number) => request<unknown>(`/reports/event/${id}/export`),
    eventsCsv: () => fetchCsv('/reports/events'),
    volunteersCsv: () => fetchCsv('/reports/volunteers'),
  },
  analytics: {
    event: (eventId: number) => request<{ sales: ApiTicketSales }>(`/analytics/event/${eventId}`),
    platform: () => request<{ events: { total: string }; tickets: { total: string; checked_in: string }; members: { total: string; vouched: string; admins: string; volunteers: string }; recent_activity: { type: string; count: string; last_activity: string }[] }>('/analytics/platform'),
  },
  settlements: {
    get: (eventId: number) => request<{
      id?: number;
      event_id: number;
      status: string;
      gross_cents: number;
      service_fee_cents: number;
      tax_cents: number;
      platform_fee_cents: number;
      host_payout_cents: number;
      expenses_cents: number;
      net_cents: number;
      preview?: boolean;
      paid_method?: string;
      paid_reference?: string;
    }>(`/settlements/${eventId}`),
    finalize: (eventId: number) => request<{ id: number }>(`/settlements/finalize`, { method: 'POST', body: { eventId } }),
    approve: (id: number) => request<{ id: number; status: string }>(`/settlements/${id}/approve`, { method: 'POST' }),
    paid: (id: number, data: { method: string; reference: string; notes?: string }) =>
      request<{ id: number; status: string; paid_method: string; paid_reference: string }>(`/settlements/${id}/paid`, { method: 'POST', body: data }),
  },
  volunteers: {
    list: (eventId: number) => request<Volunteer[]>(`/volunteers/event/${eventId}`),
    create: (data: { eventId: string | undefined; role: string; hours_planned: number; notes: string }) => request<Volunteer>('/volunteers', { method: 'POST', body: data }),
    updateStatus: (id: number, status: string) => request<Volunteer>(`/volunteers/${id}`, { method: 'PATCH', body: { status } }),
  },
  checkins: {
    list: () => request<CheckIn[]>('/checkins'),
    create: (ticketId: number) => request<CheckIn>('/checkins', { method: 'POST', body: { ticketId } }),
    checkout: (id: number) => request<CheckIn>(`/checkins/${id}/checkout`, { method: 'POST' }),
    statusForTicket: (ticketId: number) => request<CheckIn | { status: string }>(`/checkins/ticket/${ticketId}`),
  },
};

async function fetchAuth<T>(endpoint: string, options: RequestOptions): Promise<{ data: T; error: string }> {
  const token = localStorage.getItem('onisland_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options?.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers,
    ...(options || {}),
    body: options?.body != null && options.body !== '' ? JSON.stringify(options.body) : undefined,
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    return { data: null as unknown as T, error: error.error || `API error: ${res.status}` };
  }
  
  const data = await res.json();
  return { data, error: '' };
}

async function fetchCsv(endpoint: string): Promise<string> {
  const token = localStorage.getItem('onisland_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  const res = await fetch(`${API_BASE}${endpoint}`, { headers });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `API error: ${res.status}`);
  }
  
  return res.text();
}

export interface ApiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ApiConciergeResponse {
  response: string;
  source: 'home_assistant' | 'rule_based' | 'llm';
}
